'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import type { ContentPage } from '@/lib/supabase';

interface ComparisonIndexProps {
  initialPages: ContentPage[];
}

const ComparisonIndex: React.FC<ComparisonIndexProps> = ({ initialPages }) => {
  const pages = initialPages;

  return (
    <div className="max-w-5xl mx-auto px-6 py-16 md:py-24">
      <header className="mb-12">
        <nav className="text-sm text-[var(--color-muted)] mb-6">
          <Link href="/" className="hover:text-[var(--color-primary)]">Home</Link>
          <span className="mx-2">/</span>
          <span>Comparisons</span>
        </nav>
        <h1 className="font-heading text-4xl md:text-5xl font-bold text-[var(--color-text)] tracking-tight mb-4">
          IntelGrader vs Alternatives
        </h1>
        <p className="text-lg text-[var(--color-muted)] max-w-2xl">
          See how IntelGrader compares to other grading and tutoring tools. Honest, side-by-side comparisons to help you choose the right platform.
        </p>
      </header>

      {pages.length === 0 ? (
        <div className="text-center py-16 text-[var(--color-muted)]">
          No comparisons yet. Check back soon!
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pages.map((page) => {
            const competitor = page.slug.replace('vs/', '');
            const competitorName = competitor.charAt(0).toUpperCase() + competitor.slice(1).replace(/-/g, ' ');

            return (
              <Link
                key={page.slug}
                href={`/${page.slug}`}
                className="group rounded-xl border border-[var(--color-border)] hover:border-[var(--color-border-hover)] transition-all bg-[var(--color-surface)] overflow-hidden"
              >
                {page.image_url && (
                  <Image
                    src={page.image_url}
                    alt={page.image_alt || page.title}
                    className="w-full h-44 object-cover"
                    width={400}
                    height={176}
                  />
                )}
                <div className="p-5">
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-[var(--color-primary-subtle)] text-[var(--color-primary)] text-xs font-semibold mb-2">
                    vs {competitorName}
                  </span>
                  <h2 className="font-heading font-semibold text-[var(--color-text)] group-hover:text-[var(--color-primary)] transition-colors text-base leading-snug mb-2">
                    {page.title}
                  </h2>
                  <p className="text-sm text-[var(--color-muted)] line-clamp-2">
                    {page.description}
                  </p>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ComparisonIndex;
